import { getFirestore, collection, query, where, limit, getDocs, orderBy } from "firebase/firestore";
import { app } from "../../../firebase-config.js";
import { ContentService } from "../../../services/content.service.js";

const db = getFirestore(app);

export const StrategyTab = async (container) => {
    container.innerHTML = `
        <div class="space-y-6">
            <!-- Header -->
            <div class="flex items-center justify-between">
                <div>
                    <h2 class="text-2xl font-bold text-gray-900">전략 & 큐레이션 (Strategy Center)</h2>
                    <p class="text-sm text-gray-500">데이터 기반 의사결정 및 장르 소싱 가이드</p>
                </div>
                <button id="btn-refresh-strategy" class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 shadow-sm transition-colors">
                    <span class="material-symbols-outlined text-[18px]">refresh</span>
                    데이터 갱신
                </button>
            </div>

            <!-- 1. Blue Ocean Finder -->
            <div class="bg-white rounded-xl shadow-sm border border-indigo-100 overflow-hidden">
                <div class="p-6 border-b border-gray-100 bg-gradient-to-r from-white to-indigo-50/30">
                    <div class="flex items-center gap-3 mb-2">
                        <div class="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                            <span class="material-symbols-outlined text-lg">water_ec</span>
                        </div>
                        <h3 class="font-bold text-gray-900">블루오션 파인더 (Supply vs Demand)</h3>
                    </div>
                    <p class="text-xs text-gray-500 ml-11">최근 활동 작품 100건을 분석하여 <span class="text-indigo-600 font-bold">공급 부족 / 수요 과잉</span> 장르를 발굴합니다.</p>
                </div>
                
                <div class="p-6">
                    <!-- Quadrant Chart Visualization (CSS Grid) -->
                    <div class="relative w-full aspect-[21/9] bg-gray-50 rounded-xl border border-gray-200 mb-6 p-4 overflow-hidden" id="blue-ocean-chart">
                        <!-- Axis Labels -->
                        <div class="absolute left-2 top-1/2 -translate-y-1/2 -rotate-90 text-[10px] uppercase text-gray-400 font-bold tracking-wider">Demand (Avg Likes)</div>
                        <div class="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] uppercase text-gray-400 font-bold tracking-wider">Supply (Count)</div>
                        
                        <!-- Quadrant Backgrounds -->
                        <div class="absolute inset-0 grid grid-cols-2 grid-rows-2 opacity-30 pointer-events-none">
                            <div class="bg-red-50 border-r border-b border-gray-200"></div> <!-- High Demand, Low Supply (Gem) -->
                            <div class="bg-orange-50 border-b border-gray-200"></div> <!-- High Demand, High Supply (Red Ocean) -->
                            <div class="bg-gray-100 border-r border-gray-200"></div> <!-- Low Demand, Low Supply (Niche) -->
                            <div class="bg-blue-50"></div> <!-- Low Demand, High Supply (Oversupply) -->
                        </div>

                        <!-- Quadrant Labels -->
                        <div class="absolute top-4 left-4 text-xs font-bold text-red-400">💎 기회의 땅</div>
                        <div class="absolute top-4 right-4 text-xs font-bold text-orange-400">🔥 레드 오션</div>
                        <div class="absolute bottom-4 left-4 text-xs font-bold text-gray-400">🍂 틈새 시장</div>
                        <div class="absolute bottom-4 right-4 text-xs font-bold text-blue-400">🌊 공급 과잉</div>

                        <!-- Bubbles Container -->
                        <div id="bubbles-container" class="absolute inset-8 relative">
                            <div class="flex items-center justify-center h-full text-gray-400 text-sm animate-pulse">데이터 분석 중...</div>
                        </div>
                    </div>

                    <!-- Insights List -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4" id="strategy-insights">
                        <!-- Dynamic Content -->
                    </div>
                </div>
            </div>

            <!-- 2. Taste Clusters -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Taste Groups -->
                <div class="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                     <h3 class="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span class="material-symbols-outlined text-pink-500">groups</span>
                        유저 취향 클러스터
                    </h3>
                    <div class="space-y-4" id="taste-clusters-list">
                         <div class="h-12 bg-gray-50 rounded animate-pulse"></div>
                         <div class="h-12 bg-gray-50 rounded animate-pulse"></div>
                    </div>
                </div>

                <!-- Action Items -->
                <div class="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl shadow-lg p-6 text-white">
                    <h3 class="font-bold text-lg mb-1 flex items-center gap-2">
                         <span class="material-symbols-outlined text-yellow-400">bolt</span>
                         Action Items
                    </h3>
                    <p class="text-indigo-200 text-xs mb-6">데이터 기반 추천 액션</p>
                    
                    <div class="space-y-3" id="action-items-list">
                         <!-- Dynamic -->
                    </div>
                </div>
            </div>
        </div>
    `;

    const chartEl = container.querySelector('#bubbles-container');
    const insightsEl = container.querySelector('#strategy-insights');
    const clusterEl = container.querySelector('#taste-clusters-list');
    const actionsEl = container.querySelector('#action-items-list');

    const loadData = async () => {
        try {
            // 1. Fetch Sample Data (Top 100 Active Series)
            // Using logic similar to RecommendationService but aggregated
            const seriesSnap = await getDocs(query(collection(db, 'series'), where('status', '==', 'published'), limit(100)));
            const seriesList = seriesSnap.docs.map(ContentService._normalizeSeries);

            // 2. Aggregate Tags (Supply & Demand)
            const tagStats = {}; // { tagName: { count: 0, totalLikes: 0, totalViews: 0 } }

            seriesList.forEach(series => {
                if (series.tags && Array.isArray(series.tags)) {
                    series.tags.forEach(tag => {
                        const t = tag.trim();
                        if (!tagStats[t]) tagStats[t] = { count: 0, likes: 0, views: 0 };
                        tagStats[t].count += 1;
                        tagStats[t].likes += (series.likes || 0);
                        tagStats[t].views += (series.views || 0);
                    });
                }
            });

            // 3. Process Metrics
            let statsArray = Object.entries(tagStats).map(([tag, stat]) => ({
                tag,
                supply: stat.count,
                demand: stat.count > 0 ? Math.round(stat.likes / stat.count) : 0, // Demand = Avg Likes per Series
                totalDemand: stat.likes
            }));

            // Filter out low data noise
            statsArray = statsArray.filter(s => s.supply >= 2);

            // Find Max for Scaling
            const maxSupply = Math.max(...statsArray.map(s => s.supply));
            const maxDemand = Math.max(...statsArray.map(s => s.demand));

            // 4. Render Bubbles
            if (statsArray.length === 0) {
                chartEl.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400">데이터 부족 (작품 태그 데이터가 필요합니다)</div>';
            } else {
                chartEl.innerHTML = statsArray.map(item => {
                    const left = (item.supply / maxSupply) * 90 + 5; // X-axis: Supply
                    const bottom = (item.demand / maxDemand) * 90 + 5; // Y-axis: Demand

                    // Color Coding Logic
                    let color = 'bg-gray-400';
                    const isHighDemand = item.demand > (maxDemand * 0.5);
                    const isLowSupply = item.supply < (maxSupply * 0.5);

                    if (isHighDemand && isLowSupply) color = 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]'; // Gem
                    else if (isHighDemand && !isLowSupply) color = 'bg-orange-400'; // Red Ocean
                    else if (!isHighDemand && !isLowSupply) color = 'bg-blue-400'; // Oversupply
                    else color = 'bg-gray-400'; // Niche

                    return `
                        <div class="absolute group cursor-pointer transition-all duration-500 hover:scale-125 z-10" 
                             style="left: ${left}%; bottom: ${bottom}%; transform: translate(-50%, 50%);">
                            <div class="w-3 h-3 md:w-4 md:h-4 rounded-full ${color} opacity-80 group-hover:opacity-100 border border-white"></div>
                            <!-- Tooltip -->
                            <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                                <strong>#${item.tag}</strong><br/>
                                Supply: ${item.supply} / Demand: ${item.demand}
                            </div>
                        </div>
                    `;
                }).join('');
            }

            // 5. Render Insights
            // Gem: High Demand, Low Supply
            const opportunities = statsArray
                .filter(s => s.demand > maxDemand * 0.5 && s.supply < maxSupply * 0.5)
                .sort((a, b) => b.demand - a.demand)
                .slice(0, 3);

            // Red Ocean: High Demand, High Supply
            const redOceans = statsArray
                .filter(s => s.demand > maxDemand * 0.5 && s.supply >= maxSupply * 0.5)
                .sort((a, b) => b.totalDemand - a.totalDemand)
                .slice(0, 3);

            insightsEl.innerHTML = `
                <div class="bg-red-50 rounded-lg p-4 border border-red-100">
                    <h4 class="font-bold text-red-800 text-sm mb-3 flex items-center gap-2">
                        <span class="material-symbols-outlined text-sm">diamond</span>
                        블루오션 (공급 부족, 인기 높음)
                    </h4>
                    <ul class="space-y-2">
                        ${opportunities.length > 0 ? opportunities.map(o => `
                            <li class="flex items-center justify-between text-sm bg-white p-2 rounded border border-red-100 shadow-sm">
                                <span class="font-bold text-gray-700">#${o.tag}</span>
                                <span class="text-xs text-red-500 font-mono">평균 좋아요 ${o.demand}개</span>
                            </li>
                        `).join('') : '<li class="text-xs text-gray-400">데이터 부족</li>'}
                    </ul>
                </div>

                <div class="bg-orange-50 rounded-lg p-4 border border-orange-100">
                    <h4 class="font-bold text-orange-800 text-sm mb-3 flex items-center gap-2">
                        <span class="material-symbols-outlined text-sm">local_fire_department</span>
                        레드오션 (경쟁 치열)
                    </h4>
                    <ul class="space-y-2">
                        ${redOceans.length > 0 ? redOceans.map(o => `
                            <li class="flex items-center justify-between text-sm bg-white p-2 rounded border border-orange-100 shadow-sm">
                                <span class="font-bold text-gray-700">#${o.tag}</span>
                                <span class="text-xs text-orange-500 font-mono">작품수 ${o.supply}개</span>
                            </li>
                        `).join('') : '<li class="text-xs text-gray-400">데이터 부족</li>'}
                    </ul>
                </div>
            `;

            // 6. Cluster (Mock for V1, utilizing the tags we found)
            // Just grouping topmost tags as "Clans"
            const topTags = statsArray.sort((a, b) => b.totalDemand - a.totalDemand).slice(0, 3);
            clusterEl.innerHTML = topTags.map((t, idx) => `
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg group hover:bg-indigo-50 transition-colors cursor-pointer">
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center font-bold text-indigo-600 text-xs shadow-sm">
                            ${idx + 1}
                        </div>
                        <div>
                            <div class="font-bold text-gray-900 text-sm group-hover:text-indigo-700">#${t.tag} 선호 그룹</div>
                            <div class="text-xs text-gray-500">추정 규모: 약 ${(t.totalDemand * 1.5).toLocaleString()}명</div>
                        </div>
                    </div>
                     <button class="px-3 py-1.5 bg-white border border-gray-200 text-xs font-bold text-gray-600 rounded hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors shadow-sm">
                        타겟 푸시 발송
                    </button>
                </div>
            `).join('');

            // 7. Action Items
            if (opportunities.length > 0) {
                actionsEl.innerHTML = `
                    <div class="p-3 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors cursor-pointer">
                        <div class="font-bold text-sm mb-1">📢 공모전 개최 추천</div>
                        <div class="text-xs text-indigo-200">주제: <span class="text-white font-bold">#${opportunities[0].tag}</span></div>
                    </div>
                     <div class="p-3 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors cursor-pointer">
                        <div class="font-bold text-sm mb-1">✍️ 작가 영입 (Sourcing)</div>
                        <div class="text-xs text-indigo-200">타겟: <span class="text-white font-bold">#${opportunities[0].tag}</span> 장르 작가</div>
                    </div>
                `;
            } else {
                actionsEl.innerHTML = `<div class="text-center text-xs text-indigo-300 py-4">데이터 분석 후 제안이 표시됩니다.</div>`;
            }


        } catch (e) {
            console.error("Strategy Load Failed", e);
            chartEl.innerHTML = `<div class="p-4 text-red-500 text-center">분석 실패: ${e.message}</div>`;
        }
    };

    const btnRefresh = container.querySelector('#btn-refresh-strategy');
    btnRefresh.onclick = async () => {
        if (btnRefresh.disabled) return;

        // 1. Loading State
        btnRefresh.disabled = true;
        btnRefresh.classList.add('opacity-70', 'cursor-not-allowed');
        const originalContent = btnRefresh.innerHTML;
        btnRefresh.innerHTML = `
            <span class="material-symbols-outlined text-[18px] animate-spin">refresh</span>
            데이터 분석 중...
        `;

        try {
            await Promise.all([
                loadData(),
                new Promise(resolve => setTimeout(resolve, 800)) // Min wait for UX
            ]);
        } finally {
            // 2. Restore
            btnRefresh.disabled = false;
            btnRefresh.classList.remove('opacity-70', 'cursor-not-allowed');
            btnRefresh.innerHTML = originalContent;
        }
    };

    // Initial Load
    await loadData();
};
